// ADMIN-INSIGHTS: one catalogue answer drawn small — in the Ask panel and as a pinned Home widget.
// `number`: the headline and its change; `bars`: one thin bar per row (people / DEs / agents, a
// DE's budget used); `line`: the day or week series (the design system's TrendLineChart);
// `table`: the rows as text. The (i) and the link say where the number comes from.
import type { OperationOutput } from "@companyos/sdk";
import { InlineBar, TrendLineChart } from "@companyos/ui";
import { formatMoney, fractionDigits } from "../format";

export type InsightResult = OperationOutput<"insights.query">;
export type InsightViz = OperationOutput<"insights.widgets.create">["viz"];

export function formatInsight(r: Pick<InsightResult, "unit" | "currency">, v: number): string {
  if (r.unit === "money") return formatMoney(v, r.currency ?? "USD");
  if (r.unit === "percent") return `${Math.round(v)}%`;
  return new Intl.NumberFormat("en-US").format(v);
}

/** "+12% vs before" (null without a comparison). */
export function changeText(r: InsightResult): string | null {
  if (r.total === null || r.previous_total === null) return null;
  if (r.previous_total === 0) return r.total === 0 ? "same as before" : "none before";
  const pct = Math.round(((r.total - r.previous_total) / r.previous_total) * 100);
  return `${pct >= 0 ? "+" : ""}${pct}% vs before`;
}

/** The draw a result suits when none was chosen. */
export function defaultViz(r: Pick<InsightResult, "spec">): InsightViz {
  if (r.spec.by === "day" || r.spec.by === "week") return "line";
  return r.spec.by === "none" ? "number" : "bars";
}

const KIND_TAG: Record<string, string> = { de: "DE", ag: "AG" };

export function InsightView({
  result: r,
  viz,
  rows = 6,
  showNote = true,
}: {
  result: InsightResult;
  viz?: InsightViz | undefined;
  /** The note line (off where the answer's text already says it). */
  showNote?: boolean;
  /** Rows shown before "N more". */
  rows?: number;
}) {
  // A draw the data cannot fill falls back to bars (a line needs two points, a number a total).
  const asked = viz ?? defaultViz(r);
  const draw =
    (asked === "line" && r.rows.length < 2) || (asked === "number" && r.total === null)
      ? "bars"
      : asked;
  const change = changeText(r);
  const headline =
    r.total === null ? null : (
      <div className="flex min-w-0 items-baseline gap-2">
        <span
          data-slot="insight-total"
          className="font-heading text-xl leading-7 font-semibold tracking-[-0.5px] tabular-nums"
        >
          {formatInsight(r, r.total)}
        </span>
        {change ? <span className="text-xs text-muted-foreground">{change}</span> : null}
      </div>
    );
  const note =
    r.note && showNote ? <p className="text-xs text-muted-foreground">{r.note}</p> : null;

  if (draw === "line" && r.rows.length > 1)
    return (
      <div data-slot="insight-view" data-viz="line" className="flex min-w-0 flex-col gap-1">
        {headline}
        <TrendLineChart
          className="h-28"
          label={`${r.title}: ${r.rows.map((x) => `${x.label} ${formatInsight(r, x.value)}`).join(", ")}`}
          // Money in major units on the axis and tooltip (the API's are minor units).
          data={r.rows.map((x) => ({
            label: x.label.replace(/^\d{4}-/, ""),
            value:
              r.unit === "money" ? x.value / 10 ** fractionDigits(r.currency ?? "USD") : x.value,
          }))}
          xKey="label"
          series={[{ key: "value", label: r.title }]}
        />
        {note}
      </div>
    );

  if ((draw === "bars" || draw === "table") && r.rows.length > 0) {
    const shown = r.rows.slice(0, rows);
    const max = Math.max(1, ...shown.map((x) => x.value));
    return (
      <div data-slot="insight-view" data-viz={draw} className="flex min-w-0 flex-col gap-1.5">
        {headline}
        <ul className="flex min-w-0 flex-col gap-1">
          {shown.map((x) => (
            <li key={x.key} data-row={x.key} className="flex min-w-0 flex-col gap-0.5">
              <div className="flex min-w-0 items-baseline gap-2 text-[13px]">
                <span className="min-w-0 flex-1 truncate">
                  {x.label}
                  {x.key.startsWith("member:") && x.kind && KIND_TAG[x.kind] ? (
                    <span className="ml-1 text-xs text-muted-foreground">{KIND_TAG[x.kind]}</span>
                  ) : null}
                </span>
                <span className="shrink-0 tabular-nums">{formatInsight(r, x.value)}</span>
              </div>
              {draw === "bars" ? (
                <InlineBar
                  size="xs"
                  value={r.unit === "percent" ? x.value : (x.value / max) * 100}
                  tone={
                    r.unit === "percent"
                      ? x.value >= 100
                        ? "danger"
                        : x.value >= 80
                          ? "warning"
                          : "primary"
                      : "primary"
                  }
                />
              ) : null}
              {x.detail ? <span className="text-xs text-muted-foreground">{x.detail}</span> : null}
            </li>
          ))}
        </ul>
        {r.rows.length > shown.length ? (
          <span className="text-xs text-muted-foreground">{r.rows.length - shown.length} more</span>
        ) : null}
        {note}
      </div>
    );
  }

  return (
    <div data-slot="insight-view" data-viz="number" className="flex min-w-0 flex-col gap-1">
      {headline ?? (
        <span className="w-fit rounded-sm border border-subtle bg-sunken px-1.5 py-0.5 text-xs text-muted-foreground">
          Nothing yet
        </span>
      )}
      {note}
    </div>
  );
}
